import { ProductsConsumer } from "@/app/products/store";
import { routes } from "@/data/routes";
import { cn } from "@/lib/components";
import { toQueryParams } from "@/lib/queryParams";
import { type ProductSearchRequest } from "@repo/schemas";
import { useRouter } from "next/navigation";
import {
  ProductFilters,
  type ProductFiltersProps,
} from "../blocks/ProductFilters";
import {
  ProductSortBy,
  type ProductSortByProps,
} from "../blocks/ProductSortBy";

export function ProductsHeaderContainer() {
  const router = useRouter();

  function updateSearchParams(nextParams: ProductSearchRequest) {
    router.push(`${routes.products.path}?${toQueryParams(nextParams)}`, {
      scroll: false,
    });
  }
  return (
    <ProductsConsumer getters={["productsSearchParams"]}>
      {({ productsSearchParams }) => (
        <aside
          aria-labelledby="filters-heading"
          className={cn(
            "grid grid-cols-1 gap-4 lg:grid-cols-[min-content_200px_300px_1fr_min-content_200px]",
            "mb-4 border-b border-b-gray-100 py-4",
          )}
        >
          <ProductFilters
            value={productsSearchParamsToFilterValue(productsSearchParams)}
            onChange={(nextFilters) => {
              updateSearchParams({
                ...productsSearchParams,
                page: 1,
                filter: {
                  category:
                    nextFilters.categories && nextFilters.categories.length
                      ? {
                          $in: nextFilters.categories,
                        }
                      : undefined,
                  price:
                    nextFilters.maxPrice || nextFilters.minPrice
                      ? {
                          $gte: nextFilters.minPrice,
                          $lte: nextFilters.maxPrice,
                        }
                      : undefined,
                },
              });
            }}
          />
          <div />
          <ProductSortBy
            value={productsSearchParamsToSortValue(productsSearchParams)}
            onChange={(nextSortBy) => {
              updateSearchParams({
                ...productsSearchParams,
                page: 1,
                sort:
                  nextSortBy === "relevance_desc"
                    ? undefined
                    : [nextSortBy.split("_") as [string, "asc" | "desc"]],
              });
            }}
          />
        </aside>
      )}
    </ProductsConsumer>
  );
}

function productsSearchParamsToFilterValue(
  productsSearchParams: ProductSearchRequest,
): ProductFiltersProps["value"] {
  return {
    categories:
      productsSearchParams.filter?.category &&
      "$in" in productsSearchParams.filter?.category
        ? productsSearchParams.filter?.category.$in
        : [],
    minPrice:
      productsSearchParams.filter?.price &&
      "$gte" in productsSearchParams.filter?.price
        ? productsSearchParams.filter?.price.$gte
        : undefined,
    maxPrice:
      productsSearchParams.filter?.price &&
      "$lte" in productsSearchParams.filter?.price
        ? productsSearchParams.filter?.price.$lte
        : undefined,
  };
}

function productsSearchParamsToSortValue(
  productsSearchParams: ProductSearchRequest,
): ProductSortByProps["value"] {
  const firstSort = productsSearchParams.sort?.[0] ?? [];
  if (firstSort?.[0] === "price" && firstSort?.[1] === "asc") {
    return "price_asc";
  } else if (firstSort?.[0] === "price" && firstSort?.[1] === "desc") {
    return "price_desc";
  } else {
    return "relevance_desc";
  }
}
